import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import CountrySelect from './CountrySelect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const formatAmount = (amount, currencyCode) => {
  const code = String(currencyCode || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
    }).format(Number(amount));
  } catch {
    return `${code} ${Number(amount).toFixed(2)}`;
  }
};

const CurrencyMinimumBalanceSection = () => {
  const [settings, setSettings] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    country_id: '',
    minimum_balance: '',
  });
  const [editingId, setEditingId] = useState(null);

  const configuredCountryIds = useMemo(
    () => new Set(settings.map((item) => item.country_id)),
    [settings]
  );

  const availableCountries = useMemo(
    () =>
      countries.filter(
        (item) => !configuredCountryIds.has(item.country_id) || editingId
      ),
    [countries, configuredCountryIds, editingId]
  );

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, countriesRes] = await Promise.all([
        axios.get(`${API_URL}/super-admin/currency-minimum-balances`),
        axios.get(`${API_URL}/super-admin/currency-minimum-balances/available-countries`),
      ]);
      setSettings(settingsRes.data || []);
      setCountries(countriesRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load minimum balance settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setForm({ country_id: '', minimum_balance: '' });
    setEditingId(null);
  };

  const handleEdit = (setting) => {
    setEditingId(setting.id);
    setForm({
      country_id: setting.country_id,
      minimum_balance: String(setting.minimum_balance),
    });
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const selectedCountry = countries.find((item) => item.country_id === form.country_id);
    const editingSetting = settings.find((item) => item.id === editingId);

    try {
      await axios.post(`${API_URL}/super-admin/currency-minimum-balances`, {
        country_id: form.country_id,
        country_name:
          selectedCountry?.country_name ||
          editingSetting?.country_name ||
          '',
        currency_code:
          selectedCountry?.currency_code || editingSetting?.currency_code || null,
        currency_label:
          selectedCountry?.currency_label || editingSetting?.currency_label || null,
        minimum_balance: Number(form.minimum_balance),
      });

      setSuccess(
        editingId
          ? 'Minimum balance updated successfully'
          : 'Minimum balance added successfully'
      );
      resetForm();
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save minimum balance');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this country minimum balance setting?')) return;

    setError('');
    setSuccess('');
    try {
      await axios.delete(`${API_URL}/super-admin/currency-minimum-balances/${id}`);
      if (editingId === id) resetForm();
      setSuccess('Minimum balance setting removed');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete minimum balance setting');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Country Minimum Balances</h1>
        <p className="text-gray-500 mt-1">
          Set the minimum vault balance per country. Companys admins receive a low balance email
          when their vault drops below the threshold for their country.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 bg-white rounded-crm border border-border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingId ? 'Edit Minimum Balance' : 'Add Minimum Balance'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <CountrySelect
                label="Country"
                value={form.country_id}
                onChange={(next) => setForm((prev) => ({ ...prev, country_id: next }))}
                countries={
                  editingId
                    ? countries.filter((item) => item.country_id === form.country_id)
                    : availableCountries
                }
                getOptionId={(item) => String(item.country_id)}
                getOptionLabel={(item) =>
                  item.currency_label
                    ? `${item.country_name} — ${item.currency_label}`
                    : item.country_name
                }
                placeholder="Select country"
                emptyLabel="Select country"
                required
                disabled={Boolean(editingId)}
                allowEmpty={!editingId}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Balance
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.minimum_balance}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, minimum_balance: event.target.value }))
                }
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                placeholder="e.g. 1000"
                required
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 btn-primary disabled:opacity-60"
              >
                {saving ? 'Saving...' : editingId ? 'Update Setting' : 'Add Setting'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="xl:col-span-2 table-shell">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Configured Thresholds</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Country</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Currency</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">
                    Minimum Balance
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Updated</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {settings.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-400">
                      No minimum balance settings yet.
                    </td>
                  </tr>
                ) : (
                  settings.map((setting) => (
                    <tr key={setting.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">
                        {setting.country_name}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {setting.currency_label || setting.currency_code?.toUpperCase() || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {formatAmount(setting.minimum_balance, setting.currency_code)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {setting.updated_at
                          ? new Date(setting.updated_at).toLocaleString()
                          : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(setting)}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-white"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(setting.id)}
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default CurrencyMinimumBalanceSection;
